<template>
  <span class="location-photo-upload">
    <TooltipWrapper :text="tooltipText" placement="top">
      <template #hoverableElement>
        <button
          type="button"
          class="location-photo-upload__button"
          :disabled="isUploadingInProgress"
          :aria-label="isUploadingInProgress ? `${tooltipText} (${props.locationName})` : `Upload photos to ${props.locationName}`"
          @click="openFilePicker"
        >
          <LoadingIcon v-if="isUploadingInProgress" :size="16" />
          <IconEmbedded v-else name="upload_2" :size="16" color="#344054" />
        </button>
      </template>
    </TooltipWrapper>

    <!-- Does the uploading; the button above is its only UI. -->
    <span class="location-photo-upload__form" aria-hidden="true">
      <UploadPhotoForm
        ref="uploadPhotoFormRef"
        callerName="localPromotionUpload"
        flow="customer-upload"
        :ownerId="props.customerId"
        :img="NO_IMAGES"
        :isThumbnailsVisible="false"
        :maxNumberOfFilesToUploadAtOnce="maxAssetsPerGroup"
        :beforeUploadCheck="checkAssetLimitBeforeUpload"
        hideVideoGuidance
        :isVideoUploadAllowed="false"
        :inputId="`local-promotion-upload-${props.locationId}`"
        @uploadingStarted="handleUploadingStarted"
        @imageUploadedToServer="handleImageUploaded"
        @uploadProgressChange="handleProgress"
      />
    </span>
  </span>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from "vue";
import { usePhotoGroupUpload } from "@composables/usePhotoGroupUpload";
import { MetricSender } from "@helpers/MetricSender";
import PhotoGalleryV2Repo from "@repos/PhotoGalleryV2Repo";
import { useToastsStore } from "@stores/toasts";

// Components
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import LoadingIcon from "@components/ui/LoadingIcon.vue";
import TooltipWrapper from "@components/ui/TooltipWrapper.vue";
import UploadPhotoForm from "@views/CMS/UploadPhotoForm.vue";

// Types
import { PhotoGallerySource } from "@contracts/photosPageData";
import type { UploadedImage } from "@contracts/uploadedImage";

// Uploads photos straight into one Location's album from the Local Promotion list, the same way a row's "Upload Photos"
// works on the upload page (LocationUploadRow): the album is found or created on the first save.
//
// CalendarAddingContent, not CalendarManual: CalendarManual is the onboarding upload, whose brand-new albums aren't
// summarized until the user clicks Proceed there (PhotoGalleryV2Controller.MarkForContentSummaryProcessing). Photos added
// here have no Proceed, so with CalendarManual a Location's first album would never reach post generation.
const UPLOAD_SOURCE = PhotoGallerySource.CalendarAddingContent;
// The form only needs somewhere to read images from; the list keeps its own counts.
const NO_IMAGES: Array<UploadedImage> = [];

const props = defineProps<{
  customerId: string;
  locationId: string;
  locationName: string;
  /** Photos the Location has now, for the per-album upload limit. */
  photoCount: number;
}>();

const emit = defineEmits<{
  (e: "uploadStateChanged", isUploading: boolean): void;
  /** After each batch is saved, newest first, so the list can update its count and previews without a reload. */
  (e: "photosSaved", assetIds: Array<string>): void;
}>();

const toastsStore = useToastsStore();

// Saving ====================================================================
// Batches all wait on the same request, so a Location's album is only asked for once. Cleared on failure so the next batch asks again.
let albumIdRequest: Promise<string> | null = null;

function ensureAlbumId(): Promise<string> {
  albumIdRequest ??= PhotoGalleryV2Repo.ensureGroupForLocation(props.customerId, props.locationId, UPLOAD_SOURCE)
    .then(gallery => gallery.id)
    .catch(error => {
      albumIdRequest = null;
      throw error;
    });
  return albumIdRequest;
}

// Retries of the same failing batch shouldn't each raise a toast.
let hasReportedSaveError = false;

async function savePhotos(assetIds: Array<string>) {
  try {
    const albumId = await ensureAlbumId();
    await PhotoGalleryV2Repo.addPhotosToGroup(props.customerId, albumId, assetIds, UPLOAD_SOURCE);
    hasReportedSaveError = false;
    emit("photosSaved", assetIds);
  } catch (error: any) {
    if (!hasReportedSaveError) {
      hasReportedSaveError = true;
      const serverMessage = typeof error?.response?.data === "string" ? error.response.data : "";
      toastsStore.error({
        message: `Couldn't save photos to ${props.locationName}`,
        details: serverMessage || "We'll keep trying. If this doesn't clear up, refresh the page.",
        expiresInMs: 30000,
      });
    }
    // The queue retries the batch.
    throw error;
  }
}

// Uploading =================================================================
const uploadPhotoFormRef = ref<InstanceType<typeof UploadPhotoForm> | null>(null);
// Uploaded this visit but not saved yet, so the limit check counts them.
const unsavedCount = ref<number>(0);

const { isUploadingInProgress, areFileUploadsComplete, maxAssetsPerGroup, handleUploadingStarted, handleAssetUploaded, handleUploadProgressChange, checkAssetLimitBeforeUpload } = usePhotoGroupUpload({
  // Must stay the same on every visit: the queue resumes saving ids left over from a visit that ended mid-save.
  storageKey: `LocalPromotionUpload-${props.locationId}`,
  saveFunction: async assetIds => {
    await savePhotos(assetIds);
    unsavedCount.value = Math.max(0, unsavedCount.value - assetIds.length);
  },
  getCurrentAssetCount: () => props.photoCount + unsavedCount.value,
  groupNoun: "album",
  getLogContext: () => `${props.customerId} locationId=${props.locationId} source=localPromotion`,
});

const uploadedFileCount = ref<number>(0);
const totalFileCount = ref<number>(0);

function handleImageUploaded(uploadedImage: UploadedImage) {
  unsavedCount.value++;
  handleAssetUploaded(uploadedImage.serverId);
}

function handleProgress(info: { isUploadInProgress: boolean; completedCount?: number; totalCount?: number }) {
  uploadedFileCount.value = info.completedCount ?? 0;
  totalFileCount.value = info.totalCount ?? 0;
  handleUploadProgressChange(info);
}

watch(isUploadingInProgress, isUploading => {
  emit("uploadStateChanged", isUploading);
  if (isUploading) MetricSender.sendMetric("Cust-Report-LocationHighlights-UploadPhotos", { customerId: props.customerId, locationId: props.locationId });
  else unsavedCount.value = 0;
});

onBeforeUnmount(() => {
  if (isUploadingInProgress.value) emit("uploadStateChanged", false);
});

function openFilePicker() {
  if (isUploadingInProgress.value) return;
  uploadPhotoFormRef.value?.clickUploadImage();
}

const tooltipText = computed<string>(() => {
  if (!isUploadingInProgress.value) return "Upload photos";
  if (areFileUploadsComplete.value) return "Saving…";
  // The form counts finished files; show the one in flight.
  return totalFileCount.value > 0 ? `Uploading ${Math.min(uploadedFileCount.value + 1, totalFileCount.value)} of ${totalFileCount.value}…` : "Uploading…";
});
</script>

<style scoped lang="scss">
// Location photo upload ======================================================
.location-photo-upload {
  display: inline-flex;
  align-items: center;

  &__button {
    width: 32px;
    height: 32px;
    padding: 0;
    display: inline-flex;
    justify-content: center;
    align-items: center;
    border: 1px solid #d0d5dd;
    border-radius: 6px;
    background: #fff;
    cursor: pointer;

    &:hover:not(:disabled) {
      border-color: #087f82;
      background: #eef6f5;
    }

    &:focus-visible {
      outline: 2px solid #087f82;
      outline-offset: 2px;
    }

    &:disabled {
      cursor: default;
    }
  }

  &__form {
    display: none;
  }
}
</style>
